import { For, Show, createMemo, createSignal, type Component } from 'solid-js';
import type { CasinoProfile } from '~/domain/types';
import { centsToInput, formatCents, parseCents, validateCasino } from '~/domain/profiles';

/**
 * Casino editor (SPEC.md §5.1): the house-wide settings — name, currency, and
 * the chip denominations dealt. Spreads are built in abstract chips, so these
 * are the values a chip can be worth when a strategy is simulated — a house
 * dealing $2.50 chips is a house you can test a strategy at $2.50 a chip.
 */

export interface CasinoEditorProps {
  casino: CasinoProfile;
  onChange: (casino: CasinoProfile) => void;
  onDelete?: () => void;
}

const CasinoEditor: Component<CasinoEditorProps> = (props) => {
  const [pending, setPending] = createSignal('');

  const patch = (changes: Partial<CasinoProfile>) =>
    props.onChange({ ...props.casino, ...changes, updatedAt: Date.now() });

  const errors = createMemo(() => validateCasino(props.casino));

  const addDenomination = () => {
    const cents = parseCents(pending());
    if (cents === null || cents <= 0) return;
    if (props.casino.denominations.includes(cents)) {
      setPending('');
      return;
    }
    patch({ denominations: [...props.casino.denominations, cents].sort((a, b) => a - b) });
    setPending('');
  };

  const removeDenomination = (cents: number) =>
    patch({ denominations: props.casino.denominations.filter((value) => value !== cents) });

  return (
    <div class="space-y-4">
      <label class="block">
        <span class="mb-1 block text-[10px] font-medium uppercase tracking-[0.12em] text-ink-faint">
          Casino name
        </span>
        <input
          type="text"
          value={props.casino.name}
          onInput={(event) => patch({ name: event.currentTarget.value })}
          class="w-full rounded border border-line bg-surface-2 px-2 py-1 text-[12px] text-ink"
        />
      </label>

      <label class="block">
        <span class="mb-1 block text-[10px] font-medium uppercase tracking-[0.12em] text-ink-faint">
          Currency
        </span>
        <input
          type="text"
          maxLength="3"
          value={props.casino.currency}
          onInput={(event) => patch({ currency: event.currentTarget.value.toUpperCase() })}
          class="w-20 rounded border border-line bg-surface-2 px-2 py-1 text-[12px] uppercase text-ink"
        />
      </label>

      <div>
        <h4 class="mb-1.5 text-[10px] font-medium uppercase tracking-[0.12em] text-ink-faint">
          Chip denominations
        </h4>
        <div class="flex flex-wrap gap-1.5">
          <For each={props.casino.denominations}>
            {(cents) => (
              <span class="tnum flex items-center gap-1 rounded-full border border-line bg-surface-2 py-0.5 pl-2.5 pr-1 text-[11px] text-ink">
                {formatCents(cents, props.casino.currency)}
                <button
                  type="button"
                  onClick={() => removeDenomination(cents)}
                  title={`Remove ${formatCents(cents, props.casino.currency)} chip`}
                  class="rounded-full px-1 text-ink-faint hover:text-loss"
                >
                  ×
                </button>
              </span>
            )}
          </For>
        </div>

        <div class="mt-2 flex gap-1.5">
          <input
            type="text"
            inputmode="decimal"
            placeholder="2.50"
            value={pending()}
            onInput={(event) => setPending(event.currentTarget.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                addDenomination();
              }
            }}
            class="tnum w-24 rounded border border-line bg-surface-2 px-2 py-1 text-[11px] text-ink"
          />
          <button
            type="button"
            onClick={addDenomination}
            disabled={parseCents(pending()) === null}
            class="rounded border border-line bg-surface-2 px-2 py-1 text-[11px] text-ink-dim enabled:hover:border-line-strong enabled:hover:text-ink disabled:opacity-40"
          >
            Add chip
          </button>
        </div>
        <p class="mt-1 text-[10px] text-ink-faint">
          Stored to the cent, so a $2.50 chip stays exact — {centsToInput(250)} becomes 250
          cents, never a rounded float.
        </p>
      </div>

      <Show when={errors().length > 0}>
        <ul class="space-y-0.5 rounded border border-loss/40 bg-loss/10 p-2 text-[11px] text-loss">
          <For each={errors()}>{(error) => <li>{error}</li>}</For>
        </ul>
      </Show>

      <Show when={props.onDelete}>
        <button
          type="button"
          onClick={() => props.onDelete?.()}
          class="w-full rounded border border-line bg-surface-2 px-2 py-1 text-[11px] text-ink-dim hover:border-loss hover:text-loss"
        >
          Delete casino and its tables
        </button>
      </Show>
    </div>
  );
};

export default CasinoEditor;
